import type { MouseEventHandler } from 'react'
import { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import ModalBase from 'LuxKit/Modal/ModalBase'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import lazyImport from 'components/utils/lazyImport'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useMobileNativeAppAuth } from 'selectors/configSelectors'
import { clearAuthError, loginNativeApp } from 'actions/AuthActions'
import setLoyaltyCustomerEnrolmentPending from 'actions/loyalty/setLoyaltyCustomerEnrolmentPending'
import { selectLoggedIn } from 'selectors/accountSelectors'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { replace } from 'lib/routing/appRouting'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import HeroImage from 'content/components/HeroImage'
import type { AccountAccessModalResult } from './AccountAccessModal.types'
import { mediaQueryDown } from 'components/utils/breakpoint'
import { trackClientEvent } from 'analytics/analytics'
import { beginSuppressGoogleOneTap, endSuppressGoogleOneTap } from 'lib/auth/googleOneTap'
import { preloadAppleSdk } from 'lib/auth/appleSdk'
import { preloadFacebookSdk } from 'lib/auth/facebookSdk'
import type { FlashOfferSignUpContentProps } from 'components/OfferPage/FlashHotel/FlashOfferSignUpContent'

// The auth forms are the bulk of this modal and are only needed once it opens,
// so they load as their own chunk rather than sitting in every page's entry.
const AccountAccessModalContent = lazyImport(
  () => import('./AccountAccessModalContent'),
  <LoadingIndicator visible floating />,
)

const HERO_HEIGHT = 200

const HeroLayer = styled.div`
  position: absolute;
  top: 0;
  inset-inline: 0;
  height: ${rem(HERO_HEIGHT)};
  overflow: hidden;
`

const StyledAccountAccessModal = styled(ModalBase)`
  &.has-mobile-hero {
    ${mediaQueryDown.mobile} {
      padding-top: ${rem(HERO_HEIGHT)};
    }
  }
`

export interface AccountAccessModalProps {
  dismissable?: boolean
  initialMode?: App.UiAccountModalMode
  resetPasswordToken?: string
  resetPasswordTokenValid?: boolean
  hotelOfferDetails?: App.AccountModalOfferDetails
  offerDetails?: App.AccountModalOfferDetails
  heroImageId?: string
  defaultEmail?: string
  autoContinueEmail?: boolean
  initialUser?: App.User
  callbackPath?: string
  defaultAppleData?: App.AppleLoginData
  hideLoginPasswordBackButton?: boolean
  /** Create-account back closes the modal instead of opening the standard login step. */
  closeOnJoinBack?: boolean
  /** Metasearch guest checkout: offers a "Continue without signing in" action on the login step. */
  guestCheckoutEnabled?: boolean
  flashOfferSignUp?: FlashOfferSignUpContentProps
  /** Opened by a Société enrol CTA, which enrols the customer itself once they're signed in. */
  skipLoyaltyAutoEnrolment?: boolean
}

function AccountAccessModal(props: AccountAccessModalProps) {
  const {
    initialMode = 'login',
    resetPasswordToken,
    resetPasswordTokenValid = true,
    hotelOfferDetails,
    offerDetails,
    heroImageId,
    dismissable = true,
    defaultEmail,
    autoContinueEmail,
    initialUser,
    callbackPath,
    defaultAppleData,
    hideLoginPasswordBackButton,
    closeOnJoinBack,
    guestCheckoutEnabled,
    flashOfferSignUp,
    skipLoyaltyAutoEnrolment,
  } = props

  const [mode, setMode] = useState<App.UiAccountModalMode>(initialMode)
  const [secondChanceDismissed, setSecondChanceDismissed] = useState(false)
  // Agent Hub sign-in forms stay open after login until the agent account loads, so keep showing progress.
  const loading = useAppSelector(state => state.auth.processing ||
    (selectLoggedIn(state) && state.agentHub.agentHubAccount.fetching))

  const { resolve, afterClose } = useModalElementContext<AccountAccessModalResult>()
  const onModeChange = useCallback((nextMode: App.UiAccountModalMode) => {
    // The Infinite login card stays open underneath this modal. Back to the
    // standard login step should close it and reveal that card.
    if (closeOnJoinBack && nextMode === 'login') {
      resolve({ loggedIn: false })
      return
    }
    setMode(nextMode)
  }, [closeOnJoinBack, resolve])
  const useNativeAppAuth = useAppSelector(useMobileNativeAppAuth)
  const loggedIn = useAppSelector(selectLoggedIn)
  const loggedInError = useAppSelector(state => state.auth.error)
  const dispatch = useAppDispatch()
  const isSecondChanceMode = mode === 'promptAddPhone' || mode === 'promptAddPhoneOnboarding' || mode === 'joinOTP'
  const shouldShowHero = Boolean(heroImageId)
  const showFlashOfferSignUp = mode === 'login' && !!flashOfferSignUp
  const resolvedHeroImageId = heroImageId ?? ''

  useLayoutEffect(() => {
    // Hide One Tap before paint and keep it suppressed while this modal is mounted.
    // GoogleAuth only learns the modal is open on afterOpen, which is too late for a
    // fast Google button click (FedCM is single-flight).
    beginSuppressGoogleOneTap()
    return () => endSuppressGoogleOneTap()
  }, [])

  useEffect(() => {
    // Start social SDKs before the lazy auth chunk mounts so the buttons can
    // enable as soon as they are ready, instead of queueing a late popup.
    preloadAppleSdk()
    preloadFacebookSdk()
  }, [])

  useEffect(() => {
    dispatch(clearAuthError())
    return () => {
      dispatch(clearAuthError())
    }
  }, [dispatch])

  useEffect(() => {
    if (!skipLoyaltyAutoEnrolment) return
    // Keeps sign-in auto-enrolment out of the way, so the CTA's enrolment records them as CUSTOMER
    dispatch(setLoyaltyCustomerEnrolmentPending(true))
    return () => {
      dispatch(setLoyaltyCustomerEnrolmentPending(false))
    }
  }, [skipLoyaltyAutoEnrolment, dispatch])

  useEffect(() => {
    if (!isSecondChanceMode && secondChanceDismissed) {
      setSecondChanceDismissed(false)
    }
  }, [isSecondChanceMode, secondChanceDismissed])

  useEffect(() => {
    if (useNativeAppAuth) {
      dispatch(loginNativeApp())
    }
  }, [useNativeAppAuth, dispatch])

  useEffect(() => {
    if (useNativeAppAuth) {
      if (loggedIn) {
        resolve({ loggedIn: true })
        // afterClose **must** be called after resolve has finished resolving (it sets up state in after resolve)
        // 'resolve' resolves a promise, causing its result to be evalulated on next tick
        // To make afterClose happen after resolve, we bump it to the next tick as well
        window.setTimeout(() => afterClose(), 0)
      } else if (loggedInError) {
        resolve({ loggedIn: false })
        window.setTimeout(() => afterClose(), 0)
      }
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [useNativeAppAuth, loggedIn, loggedInError])

  const afterCloseHandler = () => {
    if (callbackPath) {
      dispatch(replace(callbackPath))
    }
  }

  const handleSecondChanceClose = useCallback<MouseEventHandler<HTMLElement>>((event) => {
    event.preventDefault()
    if (!secondChanceDismissed) {
      setSecondChanceDismissed(true)
      return
    }

    if (mode === 'promptAddPhoneOnboarding') {
      trackClientEvent({
        subject: 'phone_prompt_dismissed',
        action: 'interaction',
        category: 'logging',
        type: 'operational',
      })
    }
    resolve({ loggedIn: true })
  }, [secondChanceDismissed, resolve, mode])

  if (useNativeAppAuth) {
    return null
  }

  return (<StyledAccountAccessModal
    size={flashOfferSignUp ? 'XL' : 'S'}
    dismissible={dismissable}
    modalId="accountAccess"
    aria-label={showFlashOfferSignUp ? flashOfferSignUp?.offer.property.name : undefined}
    onAfterClose={afterCloseHandler}
    onClose={isSecondChanceMode ? handleSecondChanceClose : undefined}
    className={shouldShowHero ? 'has-mobile-hero' : undefined}
  >
    <LoadingIndicator visible={loading} floating />
    {shouldShowHero && (
      <CSSBreakpoint max="mobile">
        <HeroLayer>
          <HeroImage
            id={resolvedHeroImageId}
            mobileAspectRatio="4:3"
            fit="center"
            loading="eager"
          />
        </HeroLayer>
      </CSSBreakpoint>
    )}
    <AccountAccessModalContent
      mode={mode}
      initialMode={initialMode}
      defaultEmail={defaultEmail}
      autoContinueEmail={autoContinueEmail}
      initialUser={initialUser}
      onModeChange={onModeChange}
      resetPasswordToken={resetPasswordToken}
      resetPasswordTokenValid={resetPasswordTokenValid}
      hotelOfferDetails={hotelOfferDetails}
      offerDetails={offerDetails}
      callbackPath={callbackPath}
      dismissable={dismissable}
      defaultAppleData={defaultAppleData}
      secondChanceDismissed={secondChanceDismissed}
      onSecondChanceDismissedChange={setSecondChanceDismissed}
      hideLoginPasswordBackButton={hideLoginPasswordBackButton}
      closeOnJoinBack={closeOnJoinBack}
      guestCheckoutEnabled={guestCheckoutEnabled}
      flashOfferSignUp={flashOfferSignUp}
    />
  </StyledAccountAccessModal>
  )
}

export default AccountAccessModal
